import { useScrumStore } from '@/lib/scrum-store';
import { useOwnerStore } from '@/lib/owner-store';
import { useState, useEffect } from 'react';
import { formatDate } from '@/lib/utils/timezone';
import { Building2, Mail, Users } from 'lucide-react';
import { ScrumUpdateActions } from './scrum-update-actions';
import { ScrumUpdateForm } from './scrum-update-form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export function ScrumList() {
  const { updates, loading, error, fetchUpdates, updateScrum, deleteScrum } = useScrumStore();
  const { owners, fetchOwners } = useOwnerStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<string>('all');

  useEffect(() => {
    fetchUpdates();
    fetchOwners();
  }, [fetchUpdates, fetchOwners]);

  if (loading) {
    return (
      <div className="flex justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">
        Error loading scrum updates: {error}
      </div>
    );
  }

  const filteredUpdates = selectedPerson === 'all' 
    ? updates 
    : updates.filter(update => update.owner_id === selectedPerson);

  if (updates.length === 0) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 p-4">
        No scrum updates found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-4 h-4 text-gray-500" />
        <Select value={selectedPerson} onValueChange={setSelectedPerson}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by team member" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Team Members</SelectItem>
            {owners.map((owner) => (
              <SelectItem key={owner.id} value={owner.id}>
                {owner.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {filteredUpdates.map((update) => (
        <div
          key={update.id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3"
        >
          {editingId === update.id ? (
            <ScrumUpdateForm
              initialData={{
                owner_id: update.owner_id,
                yesterday: update.yesterday,
                today: update.today,
                blockers: update.blockers,
                date: update.date
              }}
              onSubmit={async (data) => {
                await updateScrum(update.id, data);
                setEditingId(null);
              }}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="font-medium">{update.owner?.name}</div>
                  {update.owner?.email && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {update.owner.email}
                    </div>
                  )}
                  {update.owner?.organization && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {update.owner.organization}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(update.date)}
                  </div>
                  <ScrumUpdateActions
                    onEdit={() => setEditingId(update.id)}
                    onDelete={() => deleteScrum(update.id)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Yesterday</h4>
                  <p className="mt-1 whitespace-pre-wrap">{update.yesterday}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Today</h4>
                  <p className="mt-1 whitespace-pre-wrap">{update.today}</p>
                </div>

                {update.blockers && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Blockers</h4>
                    <p className="mt-1 whitespace-pre-wrap">{update.blockers}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}